import React, { useCallback, useEffect } from "react";
import {
    GridRowsProp,
    GridRowModesModel,
    GridRowModes,
    DataGrid,
    GridColDef,
    GridToolbarContainer,
    GridActionsCellItem,
    GridEventListener,
    GridRowId,
    GridRowModel,
    GridRowEditStopReasons,
    GridToolbarExport,
    GridToolbarColumnsButton,
    GridToolbarFilterButton,
    GridFilterModel,
    GridSortModel
} from "@mui/x-data-grid";
import Box from "@mui/material/Box";
import Button from "@mui/material/Button";

import { Add, Edit, Delete, Save, Cancel, UploadFile } from "@mui/icons-material";
import { ThumbDown, ThumbUp, Verified } from "@mui/icons-material";
import {
    Chat,
    ChatsResponse,
    addVerifiedQuestion,
    getChats,
    updateChat,
    NewVerifiedQuestion,
    deleteChat,
    uploadVerifiedQuestionFile,
    getDomainList,
    DomainListResponse,
    DomainOption
} from "../../api";
import Snackbar from "@mui/material/Snackbar";
import { Alert, AlertProps, Checkbox, Container, Dialog, FormControlLabel, Stack, TextField, Typography } from "@mui/material";
import {getToken, useAuthentication, useLogin} from "../../authConfig";
import { useMsal } from "@azure/msal-react";
import {DOMAIN_ID_ROLE_DICT} from '../../constants';

interface EditToolbarProps {
    setRows: (newRows: (oldRows: GridRowsProp) => GridRowsProp) => void;
    setRowModesModel: (newModel: (oldModel: GridRowModesModel) => GridRowModesModel) => void;
    fileUpload: (e: React.ChangeEvent<HTMLInputElement>) => void;
    refreshTable: (page: number, size: number) => void;
    setLoading: (loading: boolean) => void;
    page: number;
    size: number;
}

function EditToolbar(props: EditToolbarProps) {
    const [snackbar, setSnackbar] = React.useState<Pick<AlertProps, "children" | "severity"> | null>(null);
    const handleCloseSnackbar = () => setSnackbar(null);
    const [newRow, setNewRow] = React.useState<NewVerifiedQuestion>({
        answer: "",
        question: "",
        gm_verified: true,
        domain_name: ""
    });
    const [newRowDialogOpen, setNewRowDialogOpen] = React.useState(false);
    const { setRows, fileUpload, refreshTable, page, size, setLoading } = props;
    const client = useLogin ? useMsal().instance : undefined;
    const { isAdmin } = useAuthentication();
    const loadingIconTimeoutMS = 6000;

    // TODO: consider fixing the local update. This updates the DB, but updating the local state doesn't work.
    // This may not be necessary since table refreshes are pretty quick now, in which case the extra code should be removed
    const handleFormSubmit = async (e: React.FormEvent<HTMLFormElement>) => {
        setLoading(true);
        e.preventDefault();
        const newChat = {
            question: newRow.question,
            answer: newRow.answer,
            gm_verified: newRow.gm_verified,
            domain_name: "" //determined in the backend based on admin role found in token
        };
        const token = client ? await getToken(client) : undefined;
        const response = await addVerifiedQuestion(token, newChat);
        if (response.status === 200) {
            const addedRow = await response.json();
            setNewRow({
                question: "",
                answer: "",
                gm_verified: true,
                domain_name: "",
            });
            setNewRowDialogOpen(false);
            setSnackbar({ children: "Row added successfully", severity: "success" });
            setRows((prevRows) => [{...addedRow, id: addedRow.chat_id, timestamp: new Date(addedRow.timestamp)}, ...prevRows]);
            // since it looks like the above fancy stuff doesn't actually work, we'll just toss in a refresh
            refreshTable(page, size);
        } else {
            setSnackbar({ children: "Error adding row", severity: "error" });
        }
        setLoading(false);
    };


    return (
        <>
            {newRowDialogOpen && (
                <Dialog open={newRowDialogOpen} onClose={() => setNewRowDialogOpen(false)}>
                    <Container maxWidth="lg" style={{ width: "30vw", margin: "10px" }}>
                        <form onSubmit={handleFormSubmit}>
                            <h3>Add Verified Question</h3>
                            <Stack spacing={2}>
                                <TextField
                                    label="Question"
                                    value={newRow.question}
                                    onChange={e => setNewRow({ ...newRow, question: e.target.value })}
                                    fullWidth
                                    required
                                    multiline
                                />
                                <TextField
                                    label="Answer"
                                    value={newRow.answer}
                                    onChange={e => setNewRow({ ...newRow, answer: e.target.value })}
                                    fullWidth
                                    required
                                    multiline
                                />
                                <FormControlLabel
                                    control={<Checkbox checked={newRow.gm_verified} onChange={e => setNewRow({ ...newRow, gm_verified: e.target.checked })} />}
                                    label="GM Verified"
                                />
                                <Stack paddingBottom={"10px"} flexDirection="row" justifyContent="left">
                                    <Button variant="outlined" onClick={() => setNewRowDialogOpen(false)} style={{ marginRight: "16px" }}>
                                        <Typography variant="button">Cancel</Typography>
                                    </Button>
                                    <Button variant="contained" type="submit">
                                        <Typography variant="button">Submit</Typography>
                                    </Button>
                                </Stack>
                            </Stack>
                        </form>
                    </Container>
                </Dialog>
            )}
            <GridToolbarContainer>
                {
                    isAdmin() && (
                        <Button color="primary" startIcon={<Add />} onClick={() => setNewRowDialogOpen(true)}>
                            Add record
                        </Button>
                    )
                }
                <GridToolbarColumnsButton />
                <GridToolbarFilterButton />
                <GridToolbarExport csvOptions={{
                    fileName: `Chat GPSC Questions ${new Date().toLocaleString("en-US", {
                        month: "2-digit",
                        day: "2-digit",
                        year: "numeric",
                        hour: "2-digit",
                        minute: "2-digit",
                        hour12: true,
                    }).replaceAll(/[,:_]/g, "").replaceAll("/", "-")}`
                }}/>
                {
                    isAdmin() && (
                        <Button component="label" startIcon={<UploadFile />}>
                            Upload Questions
                            <input onChange={fileUpload} type="file" hidden accept=".xlsx, .csv" />
                        </Button>
                    )
                }
            </GridToolbarContainer>
            {!!snackbar && (
                <Snackbar open anchorOrigin={{ vertical: "bottom", horizontal: "center" }} onClose={handleCloseSnackbar} autoHideDuration={loadingIconTimeoutMS}>
                    <Alert {...snackbar} onClose={handleCloseSnackbar} />
                </Snackbar>
            )}
        </>
    );
}

const VerifiedQuestionTable = () => {
    const [page, setPage] = React.useState<number>(1);
    const [size, setSize] = React.useState<number>(20);
    const [rows, setRows] = React.useState<GridRowsProp>([]);
    const [rowCount, setRowCount] = React.useState<number>(0);
    const [domainCategory, setDomainCategory] = React.useState<string>("");
    const [snackbar, setSnackbar] = React.useState<Pick<AlertProps, "children" | "severity"> | null>(null);
    const [rowModesModel, setRowModesModel] = React.useState<GridRowModesModel>({});
    const [loading, setLoading] = React.useState<boolean>(true);
    const client = useLogin ? useMsal().instance : undefined;
    const { isAdmin, isDomainAdmin } = useAuthentication();
    const [sortModel, setSortModel] = React.useState<GridSortModel>([]);
    const [filterModel, setFilterModel] = React.useState<GridFilterModel>({
        items: [{ field: 'domain_cat', operator: 'contains', value: domainCategory }],
    });

    const refreshTable = async (page: number, size: number) => {
        try {
            const token = client ? await getToken(client) : undefined;
            const chatResponse = await getChats(token, page, size);
            const domainResponse = await getDomainList(token);
            const data = (await chatResponse.json()) as ChatsResponse;
            const domainData = (await domainResponse.json()) as DomainListResponse;
            const domainDict: { [key: number]: string } = {};
            domainData.domains.forEach((domain: DomainOption) => {
                domainDict[parseInt(domain.domain_id)] = domain.domain_name;
            });
            const chats = data.chats;
            setRows(chats.map((row: Chat, index: number) => ({ ...row, id: row["chat_id"], timestamp: new Date(row["timestamp"]) })));
            setRowCount(data.totalCount);
            setDomainCategory(domainDict[data.domainId]);
            setFilterModel({
                items: [{ field: 'domain_cat', operator: 'contains', value: domainDict[data.domainId] }],
            });
        } catch (error) {
            setSnackbar({ children: "Error fetching data", severity: "error" });
        } finally {
            setLoading(false);
        }
    };

    const fileUpload = async (e: React.ChangeEvent<HTMLInputElement>) => {
        setLoading(true);
        const file = e.target.files?.[0];
        if (!file) {
            return;
        }
        try {
            const token = client ? await getToken(client) : undefined;
            const response = await uploadVerifiedQuestionFile(token, file);
            if (response.status === 200) {
                // get response data
                const data = await response.json();
                const insertedRows = data.inserted_rows;
                await refreshTable(page, size);
                setSnackbar({
                    children: !insertedRows
                        ? "Excel file contains no new verified questions, all of them exist in the database!"
                        : "File uploaded successfully inserted " + insertedRows + " new verified questions.",
                    severity: "success"
                });
            } else {
                setSnackbar({ children: "Error uploading file", severity: "error" });
            }
        } catch (error) {
            setSnackbar({ children: "Error uploading file", severity: "error" });
        } finally {
            e.target.value = "";
            setLoading(false);
        }
    };

    useEffect(() => {
        setLoading(true);
        refreshTable(page, size);
    }, [page, size]);

    const handleCloseSnackbar = () => setSnackbar(null);

    const handleRowEditStop: GridEventListener<"rowEditStop"> = (params, event) => {
        if (params.reason === GridRowEditStopReasons.rowFocusOut) {
            event.defaultMuiPrevented = true;
        }
    };

    const handleEditClick = (id: GridRowId) => () => {
        setRowModesModel({ ...rowModesModel, [id]: { mode: GridRowModes.Edit } });
    };

    const handleSaveClick = (id: GridRowId) => () => {
        setRowModesModel({ ...rowModesModel, [id]: { mode: GridRowModes.View } });
    };

    const handleDeleteClick = (id: GridRowId) => async () => {
        setLoading(true);
        try {
            const row = rows.find(row => row.id === id);
            const token = client ? await getToken(client) : undefined;
            const response = await deleteChat(token, id.toString());
            if (response.status !== 200) {
                throw new Error("Failed to delete verified question!");
            }
            setSnackbar({ children: "Row deleted successfully", severity: "success" });
            setRows(rows.filter(row => row.id !== id));
        } catch (error) {
            setSnackbar({ children: "Error deleting row", severity: "error" });
        }
        finally {
            setLoading(false);
        }
    };

    const handleCancelClick = (id: GridRowId) => () => {
        setRowModesModel({
            ...rowModesModel,
            [id]: { mode: GridRowModes.View, ignoreModifications: true }
        });

        const editedRow = rows.find(row => row.id === id);
        if (editedRow!.isNew) {
            setRows(rows.filter(row => row.id !== id));
        }
    };

    const mutateRow = async (newRow: GridRowModel): Promise<Response> => {
        let response: Response;
        try {
            const token = client ? await getToken(client) : undefined;
            response = await updateChat(token, newRow.id, newRow as Chat);
        } catch (error) {
            throw error;
        }
        return response;
    };

    const processRowUpdate = useCallback(
        async (newRow: GridRowModel, previousRow: GridRowModel) => {
            try {
                const updatedRow = { ...newRow, isNew: false };
                const response = await mutateRow(newRow);
                if (response.status !== 200) {
                    const data = await response.json();

                    handleProcessRowUpdateError(new Error(data.error) as Error);
                    return previousRow;
                } else {
                    await refreshTable(page, size);
                    setSnackbar({ children: "Row updated successfully", severity: "success" });
                }
                return updatedRow;
            } catch (error) {
                handleProcessRowUpdateError(error as Error);
                return previousRow;
            }
        },
        [mutateRow]
    );

    const handleRowModesModelChange = (newRowModesModel: GridRowModesModel) => {
        setRowModesModel(newRowModesModel);
    };

    const handleProcessRowUpdateError = React.useCallback((error: Error) => {
        setSnackbar({ children: error.message, severity: "error" });
    }, []);

    const columns: GridColDef[] = [
        {
            field: "chat_id",
            headerName: "Chat ID",
            width: 200,
            editable: false,
            hideable: true
        },
        {
            field: "question",
            headerName: "Question",
            width: 300,
            editable: true
        },
        {
            field: "answer",
            headerName: "Answer",
            width: 300,
            editable: true
        },
        {
            field: "is_like",
            headerName: "Liked",
            type: "boolean",
            width: 100,
            editable: true,
            renderCell: params => {
                if (params.value === true) {
                    return <ThumbUp color="primary" />;
                }
                if (params.value === false) {
                    return <ThumbDown color="secondary" />;
                }
                return null;
            }
        },
        {
            field: "gm_verified",
            headerName: "GM Verified",
            type: "boolean",
            width: 100,
            editable: true,
            renderCell: params => {
                if (params.value === true) {
                    return <Verified color="primary" />;
                }
                return null;
            }
        },
        {
            field: "domain_cat",
            headerName: "Domain",
            width: 200,
            editable: false
        },
        {
            field: "additional",
            headerName: "Additional Feedback",
            width: 300,
            editable: true
        },
        {
            field: "timestamp",
            type: "dateTime",
            headerName: "Timestamp",
            width: 200,
            editable: false,
            valueGetter: (value, row) => {
                const timestamp = row.timestamp;
                return timestamp instanceof Date ? timestamp: new Date(timestamp);
            }
        },
        ...(isAdmin() ? [{
            field: "actions",
            headerName: "Actions",
            width: 100,
            cellClassName: "actions",
            disableColumnMenu: true,
            renderCell: (params: { id: GridRowId; row: { domain_id: number; } }) => {
                const isInEditMode = rowModesModel[params.id]?.mode === GridRowModes.Edit;
                const domainAdminRole = DOMAIN_ID_ROLE_DICT.get(params.row.domain_id?.toString())
                const userHasEditPermission = domainAdminRole ? isDomainAdmin(domainAdminRole) : false;

                if (isInEditMode) {
                    return (
                        <>
                            <GridActionsCellItem
                                icon={<Save />}
                                label="Save"
                                sx={{ color: "primary.main" }}
                                onClick={handleSaveClick(params.id)}
                                disabled={!userHasEditPermission}
                            />
                            <GridActionsCellItem
                                icon={<Cancel />}
                                label="Cancel"
                                className="textPrimary"
                                onClick={handleCancelClick(params.id)}
                                color="inherit"
                                disabled={!userHasEditPermission}
                            />
                        </>
                    );
                }

                return isAdmin() ? [
                    <GridActionsCellItem
                        key={`${params.id}-edit`}
                        icon={<Edit
                                sx={{
                                    color: !userHasEditPermission ? "#00000042" : "#000000de",
                                }}
                            />
                        }
                        label="Edit"
                        onClick={handleEditClick(params.id)}
                        color="inherit"
                        disabled={!userHasEditPermission}
                    />,
                    <GridActionsCellItem
                        key={`${params.id}-delete`}
                        icon={<Delete />}
                        label="Delete"
                        onClick={handleDeleteClick(params.id)}
                        color="inherit"
                        disabled={!userHasEditPermission}
                    />
                ] : [];
            }
        }] : [])
    ];

    return (
        <Box
            sx={{
                height: 800,
                width: "100%",
                "& .actions": {
                    color: "text.secondary"
                },
                "& .textPrimary": {
                    color: "text.primary"
                },
                '& .MuiDataGrid-cell': {
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'left',
                }
            }}
        >
            <DataGrid
                rows={rows}
                columns={columns}
                pageSizeOptions={[25, 50, 100]}
                pagination
                sortModel={sortModel}
                filterModel={filterModel}
                editMode="row"
                sortingMode="client"
                filterMode="client"
                onSortModelChange={setSortModel}
                onFilterModelChange={setFilterModel}
                rowModesModel={rowModesModel}
                onRowModesModelChange={handleRowModesModelChange}
                onRowEditStop={handleRowEditStop}
                processRowUpdate={processRowUpdate}
                checkboxSelection
                disableRowSelectionOnClick
                slots={{
                    toolbar: () =>
                        <EditToolbar
                            setRows={setRows}
                            setRowModesModel={setRowModesModel}
                            fileUpload={fileUpload}
                            refreshTable={refreshTable}
                            page={page}
                            size={size}
                            setLoading={setLoading}
                        />
                }}
                loading={loading}
                initialState={{
                    columns: {
                        columnVisibilityModel: {
                            chat_id: false,
                            additional: false,
                            correct_answer: false,
                            harm_reason: false,
                            is_harm: false,
                            actions: isAdmin()
                        }
                    }
                }}
                getRowHeight={() => "auto"}
            />
            {!!snackbar && (
                <Snackbar open anchorOrigin={{ vertical: "bottom", horizontal: "center" }} onClose={handleCloseSnackbar} autoHideDuration={6000}>
                    <Alert {...snackbar} onClose={handleCloseSnackbar} />
                </Snackbar>
            )}
        </Box>
    );
};

export default VerifiedQuestionTable;
